import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './MovementDetail.css';
import { getAccessToken } from '../utils/auth';
import { getMovementDetail, getMovementTrack } from '../utils/httpApi';
import AircraftTrack from './AircraftTrack';

/**
 * Per-movement detail (spec §4.2) — a modal opened from a dashboard movement
 * row. Shows the aircraft, the flight, the landing → stand → take-off
 * timeline, the services used, the pax count and the itemised fee breakdown
 * with each charge's calculation.
 */

function apiOk(resp) {
  const s = resp && resp.header && resp.header.apiStatus;
  return s === 'OK' || s === 'SUCCESS';
}

function money(amount, currency) {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency', currency: currency || 'EUR'
    }).format(n);
  } catch {
    return (currency || 'EUR') + ' ' + n.toFixed(2);
  }
}

function num(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}

/** One timeline row, rendered only when the timestamp is present. */
function TimelineRow({ label, time, extra }) {
  if (!time) return null;
  return (
    <div className="mvd-tl-row">
      <span className="mvd-tl-label">{label}</span>
      <span className="mvd-tl-time">{time}</span>
      {extra && <span className="mvd-tl-extra">{extra}</span>}
    </div>
  );
}

/** Fits the Leaflet map to the track's bounds once the points are loaded. */
function FitTrack({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const lats = points.map((p) => p.latitude);
    const lons = points.map((p) => p.longitude);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]],
      { padding: [26, 26] }
    );
  }, [points, map]);
  return null;
}

function MovementDetail({ airportIcao, movementId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [track, setTrack] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [showTrack, setShowTrack] = useState(false);

  const load = useCallback(() => {
    const token = getAccessToken();
    if (!token) { setError('Not authenticated.'); return; }
    setLoading(true);
    setError(null);
    getMovementDetail(airportIcao, movementId, token)
      .then((resp) => {
        if (!apiOk(resp) || !resp.detail) {
          const errs = resp && resp.header && resp.header.errors;
          setError(errs && errs[0] ? errs[0].description : 'Could not load movement detail.');
          return;
        }
        setDetail(resp.detail);
      })
      .catch((e) => setError('Load error: ' + e.message))
      .finally(() => setLoading(false));
  }, [airportIcao, movementId]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lazily load the GreptimeDB track + event markers on first "Show track".
  const loadTrack = useCallback(() => {
    setShowTrack(true);
    if (track) return;
    const token = getAccessToken();
    if (!token) return;
    setTrackLoading(true);
    getMovementTrack(airportIcao, movementId, token)
      .then((resp) => {
        setTrack(apiOk(resp)
          ? { points: resp.points || [], events: resp.events || [] }
          : { points: [], events: [] });
      })
      .catch(() => setTrack({ points: [], events: [] }))
      .finally(() => setTrackLoading(false));
  }, [airportIcao, movementId, track]);

  const d = detail;
  const currency = d ? d.currency : 'EUR';

  return (
    <div className="mvd-backdrop" onClick={onClose}>
      <div className="mvd-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mvd-head">
          <div>
            <h3 className="mvd-title">
              {d ? (d.registration || d.icaoAddress || 'Movement') : 'Movement'}
              {d && d.aircraftType ? ' · ' + d.aircraftType : ''}
            </h3>
            <div className="mvd-sub">
              {d && d.airline ? d.airline : 'Unknown airline'}
              {d && d.flightCallsign ? ' · ' + d.flightCallsign : ''}
            </div>
          </div>
          <button className="mvd-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <div className="mvd-status">Loading…</div>}
        {error && <div className="mvd-status mvd-error">{error}</div>}

        {d && (
          <div className="mvd-body">
            <div className="mvd-grid">
              <div className="mvd-card">
                <div className="mvd-card-label">Aircraft</div>
                <dl className="mvd-dl">
                  <dt>Registration</dt><dd>{d.registration || '—'}</dd>
                  <dt>Type</dt><dd>{d.aircraftType || '—'}</dd>
                  <dt>Model</dt>
                  <dd>{[d.manufacturer, d.model].filter(Boolean).join(' ') || '—'}</dd>
                  <dt>ICAO address</dt><dd>{d.icaoAddress || '—'}</dd>
                  <dt>Flight</dt><dd>{d.flightCallsign || '—'}</dd>
                </dl>
              </div>

              <div className="mvd-card">
                <div className="mvd-card-label">Movement</div>
                <div className="mvd-timeline">
                  <TimelineRow label="Touchdown" time={d.touchdownAt}
                    extra={d.touchdownRunway ? 'rwy ' + d.touchdownRunway : null} />
                  <TimelineRow label="Stand arrival" time={d.standArrivalAt}
                    extra={d.standId ? 'stand ' + d.standId : null} />
                  <TimelineRow label="Stand departure" time={d.standDepartureAt} />
                  <TimelineRow label="Take-off" time={d.takeOffAt}
                    extra={d.takeoffRunway ? 'rwy ' + d.takeoffRunway : null} />
                </div>
                <dl className="mvd-dl">
                  <dt>Time on ground</dt>
                  <dd>{d.groundTimeMinutes != null ? d.groundTimeMinutes + ' min' : '—'}</dd>
                  <dt>Stand</dt>
                  <dd>{[d.standId, d.standType, d.zone].filter(Boolean).join(' · ') || '—'}</dd>
                  <dt>Passengers</dt>
                  <dd>{d.paxCount != null ? num(d.paxCount) + ' (est.)' : '—'}</dd>
                  <dt>Services</dt>
                  <dd>{d.services && d.services.length ? d.services.join(', ') : '—'}</dd>
                </dl>
              </div>
            </div>

            <div className="mvd-card">
              <div className="mvd-card-label">Fees</div>
              <table className="mvd-table">
                <thead>
                  <tr>
                    <th>Category</th><th>Event</th><th>Calculation</th>
                    <th className="num">Units</th><th className="num">Rate</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {d.charges.map((c, i) => (
                    <tr key={i}>
                      <td>{c.category}</td>
                      <td>{c.movementType || '—'}</td>
                      <td className="mvd-basis">{c.basisDetail || c.basis}</td>
                      <td className="num">{num(c.units)}</td>
                      <td className="num">{c.rate != null ? num(c.rate) : '—'}</td>
                      <td className="num">{money(c.amount, c.currency || currency)}</td>
                    </tr>
                  ))}
                  {d.charges.length === 0 && (
                    <tr><td colSpan={6} className="mvd-empty">
                      No charges — reference data (MTOW, stand type, noise QC)
                      may be missing for this aircraft.
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="mvd-total-label">Total</td>
                    <td className="num mvd-total">{money(d.totalAmount, currency)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mvd-card">
              <div className="mvd-card-label mvd-track-head">
                <span>Track</span>
                {!showTrack && (
                  <button type="button" className="mvd-track-btn" onClick={loadTrack}>
                    Show track
                  </button>
                )}
              </div>
              {trackLoading && <div className="mvd-status">Loading track…</div>}
              {showTrack && !trackLoading && track && track.points.length > 0 && (
                <div className="mvd-map">
                  <MapContainer
                    center={[track.points[0].latitude, track.points[0].longitude]}
                    zoom={12}
                    scrollWheelZoom={false}
                    style={{ height: '340px', width: '100%' }}
                  >
                    <TileLayer
                      url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; OpenStreetMap contributors"
                    />
                    <FitTrack points={track.points} />
                    <AircraftTrack trackData={{ points: track.points }} />
                    {track.events.map((ev, i) => (
                      <CircleMarker
                        key={i}
                        center={[ev.latitude, ev.longitude]}
                        radius={7}
                        pathOptions={{ color: '#ffffff', weight: 2,
                          fillColor: '#ff7a18', fillOpacity: 1 }}
                      >
                        <Tooltip permanent direction="top" offset={[0, -6]}>
                          {ev.label}
                        </Tooltip>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                </div>
              )}
              {showTrack && !trackLoading && track && track.points.length === 0 && (
                <div className="mvd-empty">No track recorded for this movement.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MovementDetail;
